import React, { useEffect, useState } from 'react';
import Button from './ui/Button';
import Icon from './ui/Icon';
import actionHref from '../lib/links';
import { nav } from '../data/content';

export default function Header() {
  // The header is transparent over the hero, then contracts into a solid bar.
  const [solid, setSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      setSolid(window.scrollY > 40);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Prevent the page scrolling behind the open mobile menu.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const compact = solid || menuOpen;

  return (
    <header
      className={`on-dark fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-out-expo ${
        compact
          ? 'border-b border-cream/10 bg-ink-900/85 backdrop-blur-md'
          : 'border-b border-transparent bg-gradient-to-b from-ink-900/80 to-transparent'
      }`}
    >
      <div
        className={`shell flex items-center justify-between gap-6 transition-all duration-500 ease-out-expo ${
          compact ? 'h-20' : 'h-28'
        }`}
      >
        <a href="#top" className="shrink-0" aria-label="TEEEM — home">
          <img
            src="/assets/logo-light.webp"
            alt="The TEEEM logo: a wooden cross forming the T in TEEEM"
            width="480"
            height="422"
            className={`w-auto transition-all duration-500 ease-out-expo ${compact ? 'h-14' : 'h-[4.5rem]'}`}
          />
        </a>

        <nav aria-label="Main" className="hidden items-center gap-8 lg:flex xl:gap-10">
          {nav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="link-underline font-display text-[0.78rem] font-medium uppercase tracking-[0.16em] text-cream/80 transition-colors duration-300 hover:text-cream"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button href={actionHref('donate')} className="hidden sm:inline-flex">
            Donate
          </Button>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="p-2 text-cream transition-colors hover:text-gold lg:hidden"
          >
            <Icon name={menuOpen ? 'close' : 'menu'} className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Height-animated so the panel slides open rather than snapping. */}
      <div
        id="mobile-menu"
        className={`overflow-hidden border-t border-cream/10 bg-ink-900 transition-[max-height,opacity] duration-500 ease-out-expo lg:hidden ${
          menuOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav aria-label="Main (mobile)" className="shell flex flex-col py-3">
          {nav.map((item, i) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              style={{ transitionDelay: menuOpen ? `${120 + i * 45}ms` : '0ms' }}
              className={`border-b border-cream/10 py-4 font-display text-sm uppercase tracking-[0.16em] text-cream/85 transition-all duration-500 ease-out-expo last:border-0 hover:text-gold ${
                menuOpen ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'
              }`}
            >
              {item.label}
            </a>
          ))}
          <Button href={actionHref('donate')} onClick={() => setMenuOpen(false)} className="mt-5 self-start sm:hidden">
            Donate
          </Button>
        </nav>
      </div>
    </header>
  );
}
